import React, { useState, useEffect, useRef } from 'react';
import { PlayIcon, PauseIcon } from '@heroicons/react/24/solid';

interface Video {
  id: string;
  title: string;
  src?: string;
  youtubeId?: string;
  thumbnail?: string;
  description?: string;
}

interface VideoGalleryProps {
  videos: Video[];
  autoPlay?: boolean;
  showThumbnails?: boolean;
}

const VideoGallery: React.FC<VideoGalleryProps> = ({ 
  videos, 
  autoPlay = true,
  showThumbnails = true 
}) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const currentVideo = videos[currentVideoIndex];
  const isYouTube = !!currentVideo.youtubeId;

  const goToVideo = (index: number) => {
    setCurrentVideoIndex(index);
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  if (videos.length === 0) {
    return null;
  }

  return (
    <div className="w-full max-w-6xl mx-auto" style={{ zIndex: 1, position: 'relative' }}>
      {/* Main Video Player */}
      <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden shadow-2xl mb-6" style={{ zIndex: 1 }}>
        {isYouTube ? (
          // YouTube iframe embed
          <iframe
            src={`https://www.youtube.com/embed/${currentVideo.youtubeId}?rel=0&modestbranding=1&autoplay=0`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            title={currentVideo.title}
          />
        ) : (
          // Local video fallback
          <video
            src={currentVideo.src}
            className="w-full h-full object-contain"
            controls
            playsInline
          />
        )}

        {/* Video Info Overlay - only for non-YouTube */}
        {!isYouTube && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <h3 className="text-white font-semibold text-lg mb-1">{currentVideo.title}</h3>
            {currentVideo.description && (
              <p className="text-white/80 text-sm">{currentVideo.description}</p>
            )}
          </div>
        )}

        {/* Video Counter */}
        <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
          {currentVideoIndex + 1} / {videos.length}
        </div>
      </div>

      {/* Video Title below for YouTube */}
      {isYouTube && (
        <div className="text-center mb-6">
          <h3 className="text-white font-semibold text-xl">{currentVideo.title}</h3>
          {currentVideo.description && (
            <p className="text-gray-400 text-sm mt-2">{currentVideo.description}</p>
          )}
        </div>
      )}

      {/* Horizontal Scrolling Video Thumbnails */}
      {showThumbnails && videos.length > 1 && (
        <div className="relative group">
          {/* Left Arrow */}
          <button
            onClick={scrollLeft}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-black/90 text-white p-3 rounded-full transition-all opacity-0 group-hover:opacity-100 -ml-4"
            aria-label="Scroll left"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth px-1"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {videos.map((video, index) => (
              <button
                key={video.id}
                onClick={() => goToVideo(index)}
                className={`relative flex-shrink-0 w-64 aspect-video rounded-lg overflow-hidden transition-all duration-300 ${
                  index === currentVideoIndex
                    ? 'ring-4 ring-cyan-400 scale-105'
                    : 'opacity-70 hover:opacity-100 hover:scale-105'
                }`}
              >
                {video.youtubeId ? (
                  // YouTube thumbnail
                  <img
                    src={`https://img.youtube.com/vi/${video.youtubeId}/mqdefault.jpg`}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                ) : video.thumbnail ? (
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center">
                    <PlayIcon className="h-8 w-8 text-white" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/20 hover:bg-black/10 transition-colors"></div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                  <p className="text-white text-xs font-medium truncate">{video.title}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Right Arrow */}
          <button
            onClick={scrollRight}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-black/90 text-white p-3 rounded-full transition-all opacity-0 group-hover:opacity-100 -mr-4"
            aria-label="Scroll right"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Video Navigation Dots (mobile-friendly alternative) */}
      {!showThumbnails && videos.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {videos.map((_, index) => (
            <button
              key={index}
              onClick={() => goToVideo(index)}
              className={`h-2 rounded-full transition-all ${
                index === currentVideoIndex
                  ? 'w-8 bg-cyan-400'
                  : 'w-2 bg-gray-600 hover:bg-gray-400'
              }`}
              aria-label={`Go to video ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default VideoGallery;
